import React, { useState } from "react";
import { toast } from "sonner";
import { api, formatApiErrorDetail } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Pencil } from "lucide-react";
import { materialFormValues, materialPayload } from "./materialForm";
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle, DialogTrigger } from "@/components/ui/dialog";

const Field = ({ id, label, field, mat, setMat, className, ...rest }) => <div className={className}><Label htmlFor={id}>{label}</Label><Input id={id} data-testid={id} value={mat[field]} onChange={(e) => setMat({ ...mat, [field]: e.target.value })} {...rest} /></div>;

/** Creazione e modifica delle proprietà del materiale, con prezzo e storico gestiti dal listino. */
export function NewMaterialDialog({ material = null, onCreated }) {
  const editing = !!material;
  const [open, setOpen] = useState(false);
  const [mat, setMat] = useState(() => materialFormValues(material));
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");
  const changeOpen = next => {
    if (busy) return;
    if (next) { setMat(materialFormValues(material)); setError(""); }
    setOpen(next);
  };
  const save = async (e) => {
    e.preventDefault();
    if (busy) return;
    let payload;
    try { payload = materialPayload(mat, { editing }); }
    catch (err) { setError(err.message); return; }
    setBusy(true); setError("");
    try {
      if (editing) await api.put(`/config/materials/${material.id}`, payload);
      else await api.post("/config/materials", payload);
      await onCreated?.();
      toast.success(editing ? "Materiale aggiornato" : "Materiale creato");
      setOpen(false);
    } catch (err) { setError(formatApiErrorDetail(err.response?.data?.detail) || "Salvataggio non riuscito. Riprova."); }
    finally { setBusy(false); }
  };
  const p = { mat, setMat };
  return (
    <Dialog open={open} onOpenChange={changeOpen}>
      <DialogTrigger asChild><Button type="button" variant={editing ? "outline" : "default"} size={editing ? "sm" : "default"} data-testid={editing ? `material-edit-${material.id}` : "btn-new-material"} aria-label={editing ? `Modifica materiale ${material.name}` : "Nuovo materiale"}>{editing ? <><Pencil size={12} className="mr-1" />Modifica</> : "+ Materiale"}</Button></DialogTrigger>
      <DialogContent className="opaque-editable-fields max-w-xl max-h-[90dvh] overflow-y-auto">
        <DialogHeader><DialogTitle>{editing ? "Modifica materiale" : "Nuovo materiale"}</DialogTitle><DialogDescription>{editing ? "Aggiorna i parametri della voce nel listino aziendale. Sfrido e maggiorazione sono separati dal prezzo €/kg." : "Voce del listino aziendale: il prezzo base €/kg può restare vuoto e risultare «Da completare»."}</DialogDescription></DialogHeader>
        <form onSubmit={save} className="grid grid-cols-1 sm:grid-cols-2 gap-3 text-sm">
          <Field {...p} id="mat-name" label="Nome" field="name" required className="sm:col-span-2" />
          <Field {...p} id="mat-norm" label="Norma" field="norm" />
          <Field {...p} id="mat-density" label="Densità kg/m³" field="density_kg_m3" inputMode="decimal" />
          {!editing && <Field {...p} id="mat-cost" label="Prezzo base €/kg (vuoto = da completare)" field="cost_per_kg" inputMode="decimal" placeholder="es. 4,50" />}
          <Field {...p} id="mat-scrap" label="Sfrido %" field="scrap_pct" inputMode="decimal" />
          <Field {...p} id="mat-surcharge" label="Maggiorazione d'acquisto %" field="surcharge_pct" inputMode="decimal" />
          <Field {...p} id="mat-supplier" label="Fornitore" field="supplier" />
          <Field {...p} id="mat-thicknesses" label="Spessori mm (separati da ;)" field="thicknesses_mm" placeholder="es. 1; 1,5; 2; 3" className="sm:col-span-2" />
          {editing && <p className="text-xs text-muted-foreground sm:col-span-2">Il prezzo €/kg e i prezzi per spessore o stato di fornitura si modificano direttamente nella tabella del listino.</p>}
          {error && <p role="alert" data-testid="mat-error" className="text-sm text-destructive sm:col-span-2">{error}</p>}
          <div className="sm:col-span-2 flex justify-end gap-2">
            <Button type="button" variant="outline" disabled={busy} onClick={() => changeOpen(false)}>Annulla</Button>
            <Button type="submit" disabled={busy} data-testid="mat-save">{busy ? "Salvataggio…" : editing ? "Salva modifiche" : "Crea materiale"}</Button>
          </div>
        </form>
      </DialogContent>
    </Dialog>
  );
}

